import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import {
    getCentred,
    getSelectedAttributes,
    getZoom,
} from "../../reducers/project/selectors";
import { AttributesType, IAttributes, Types } from "../../reducers/project";
import useDesktop from "../../components/Responsive/useDesktop";
import Draggable, { DraggableData } from "react-draggable";

const Wrapper = styled.div<any>`
  display: flex;
  align-items: center;
  transform: scale(${({ zoom }) => zoom});
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const Img = styled.img<any>`
  height: auto;
  width: 100%;
  transition: transform 0.3s ease-out;
  z-index: 1000;
  transform: scaleX(${({ fliped }) => (fliped ? -1 : 1)});
  box-shadow: ${({ shadow }) =>
    shadow ? "30px 20px 35px -10px #353535" : "unset"};
`;

function Visualizer({
                        preview,
                        width,
                        wrapperStyle,
                        small,
                        offsetHeight = 72,
                    }: IVisualizer) {
    const [pos, setPos] = useState({ x: 0, y: 0 });
    const [bouds, setBounds] = useState({ left: 0, right: 0, top: 0, bottom: 0 });
    const ref = React.useRef((null as unknown) as HTMLDivElement);
    const dragRef = React.useRef((null as unknown) as HTMLDivElement);
    const contenerRef = React.useRef((null as unknown) as HTMLDivElement);
    const [fliped, setFliped] = useState(false);
    let zoom = useSelector(getZoom);
    let centred = useSelector(getCentred);

    const selectedStore = useSelector(getSelectedAttributes);

    const [selected, setSelected] = useState(selectedStore);

    useEffect(() => {
        if (!preview) {
            setSelected(selectedStore);
        }
    }, [selectedStore]);

    useEffect(() => {
        if (preview != undefined) {
            setSelected(preview);
        }
    }, [preview]);

    const onDrag = (_: any, data: DraggableData) => {
        setPos({ x: data.x, y: data.y });
    };

    useEffect(() => {
        const { width, height } = ref.current.getBoundingClientRect();
        const {
            width: cwidth,
            height: cheight,
        } = contenerRef.current.getBoundingClientRect();

        const horizontal = (width - cwidth) / 2;
        const vertical = (height - cheight) / 2;

        setBounds({
            left: -horizontal,
            right: horizontal,
            top: -vertical,
            bottom: vertical,
        });

        let { x, y } = pos;

        if (pos.x > horizontal) {
            x = horizontal;
        }
        if (pos.x < -horizontal) {
            x = -horizontal;
        }

        if (pos.y > vertical) {
            y = vertical;
        }
        if (pos.y < -vertical) {
            y = -vertical;
        }

        setPos({ x: x, y: y });
    }, [zoom]);

    const onDragStop = (_: any, data: DraggableData) => {};

    useEffect(() => {
        setPos({ x: 0, y: 0 });
    }, [centred]);

    const desktop = useDesktop();

    useEffect(() => {
        setFliped(selected.some((a) => a.name === "left"));
    }, [selected]);

    return (
        <div
            ref={contenerRef}
            style={{
                height: !!preview
                    ? `calc(100% - ${offsetHeight}px)`
                    : "calc(100% - 144px)",
                width: width || "100%",
                overflow: "hidden",
                ...wrapperStyle,
            }}
        >
            <Draggable
                scale={zoom}
                position={pos}
                onDrag={onDrag}
                onStop={onDragStop}
                defaultPosition={{ x: 0, y: 0 }}
                disabled={!!preview || zoom === 1}
                bounds={bouds}
            >
                <div
                    ref={dragRef}
                    style={{ height: "100%", width: "100%", cursor: "move" }}
                >
                    <Wrapper zoom={!!preview ? 1 : zoom} ref={ref} draggable={false}>
                        {(selected as any)
                            .filter((s) => s?.type == Types.Select)
                            .map((s, i) => (
                                <div
                                    key={s.name + i + fliped}
                                    style={{
                                        zIndex: mapCategoryToZIndex(s?.category),
                                        position: "absolute",
                                        width: s?.vimg?.includes("jpeg")
                                            ? small
                                                ? "154px"
                                                : "180px"
                                            : small
                                                ? "180px"
                                                : "210px",
                                        marginTop: s?.vimg?.includes("jpeg") ? "12px" : "0",
                                    }}
                                >
                                    <Img
                                        shadow={s?.category == AttributesType.Ferrule}
                                        fliped={fliped}
                                        draggable={false}
                                        src={s?.vimg}
                                    />
                                </div>
                            ))}
                    </Wrapper>
                </div>
            </Draggable>
        </div>
    );
}

const mapCategoryToZIndex = (category: AttributesType): number => {
    switch (category) {
        case AttributesType.Ferrule:
            return 10;
        case AttributesType.Color:
            return 20;
        case AttributesType.Jamb:
            return 30;
        case AttributesType.Closer:
            return 90;
        case AttributesType.Handle:
            return 91;
        case AttributesType.Vent:
            return 92;
        case AttributesType.Count:
            return 0;
        case AttributesType.OpenDirection:
            return 0;
        case AttributesType.Size:
            return 0;
        default:
            return 100;
    }
};

export interface IVisualizer {
    preview?: IAttributes[];
    width?: string;
    wrapperStyle?: React.CSSProperties;
    small?: boolean;
    offsetHeight?: number;
}

export default Visualizer;
